import { useEffect, useRef, useState, type ReactElement } from "react";
import { LIMB_IDS, NEEDS_LOW, NEEDS_MAX } from "@rietz/shared";
import { TEAM_COLOR } from "../render/palette.js";
import { cssHex } from "../render/poiPalette.js";
import { InventoryScreen } from "./inventory/screen.js";
import { INVENTORY_CSS } from "./inventory/style.js";
import { LIMB_BOX_H, LIMB_BOX_W, LIMB_LABELS, LIMB_LAYOUT, limbColor } from "./limbs.js";
import { formatMmSs } from "./meta/format.js";
import { MetaScreen } from "./meta/screen.js";
import { META_CSS } from "./meta/style.js";
import { WORKBENCH_CSS } from "./workbench/style.js";
import { HideoutHud } from "./hideout/hideoutHud.js";
import { SettingsOverlay } from "./settings.js";
import { closeTopDialog, useHud, type DeathInfo, type HitMarkerKind } from "./store.js";
import { FONT, PANEL_BG, PANEL_LINE, TEXT, TEXT_DIM } from "./theme.js";

/**
 * The heads-up display: small independent pieces pinned to the edges, each
 * subscribing to only the slice it draws. Labels are one English word.
 * Inline-styled; the two things that need stylesheet features (keyframes,
 * `:hover`) live in the single `<style>` block below, which the inventory
 * screen's rules are concatenated into.
 */

export interface HudProps {
  /** Called by the death overlay's button. Wired to `sendRespawn` in main.ts. */
  onRespawn: () => void;
}

export function Hud(props: HudProps): ReactElement {
  return (
    <>
      <style>{HUD_CSS + INVENTORY_CSS + META_CSS + WORKBENCH_CSS}</style>
      <DialogEscape />
      <LimbPanel />
      <NeedsPanel />
      <AmmoPanel />
      <SquadStrip />
      <RaidTimer />
      <ExtractReadout />
      <HitMarker />
      <MedCastBar />
      <InteractPrompt />
      {/* ABOVE the four corner widgets and BELOW the inventory screen. A player
          who has been flashed cannot read their own health bar — that is the
          point — but the bag and the death overlay are both things the game
          needs to stay usable through it. */}
      <FlashOverlay />
      {/* Renders itself away while `invOpen` is false — the flag is a store
          subscription of its own, so opening the bag re-renders the screen and
          not the four corner widgets. */}
      <InventoryScreen />
      {/* Escape, in a raid. Renders itself away unless the game says it is up
          and we are actually in the world — see `SettingsOverlay`. Above the
          inventory screen and below the death overlay, which is the order the
          three of them close in. */}
      <SettingsOverlay />
      <DeathOverlay onRespawn={props.onRespawn} />
      {/* The walkable cellar's own layer: the crosshair, the Esc hint, the
          range scoreboard, a station's panel and the gym overlay. Renders
          nothing at all unless `screen` is "hideout", so a raid — and every
          direct session, which can never be on that screen — pays one store
          subscription for it and nothing else. Above the raid's overlays
          (which are all inert in there) and below the menu, which the cellar
          is a room of rather than a replacement for. */}
      <HideoutHud />
      {/* LAST, and that is load-bearing: in meta mode the death overlay may
          flash for the tick between the `death` event and the settlement, and
          the summary screen has to cover it. Renders nothing at all while
          `screen` is a raid one, which is the only thing a direct server has. */}
      <MetaScreen />
    </>
  );
}

// ---------------------------------------------------------------------------
// Escape closes the topmost window
// ---------------------------------------------------------------------------

/**
 * One listener for every screen: Escape takes down the topmost floating
 * window and is swallowed when it did; otherwise it passes to whoever is next.
 * Capture phase on `document`: after the inventory's drag cancel (capture on
 * `window`), before the game's bubble-phase handler.
 */
function DialogEscape(): null {
  useEffect(() => {
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.code !== "Escape" || ev.repeat) return;
      if (!closeTopDialog()) return;
      ev.preventDefault();
      ev.stopPropagation();
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
    };
  }, []);
  return null;
}

// ---------------------------------------------------------------------------
// Health
// ---------------------------------------------------------------------------

/**
 * Three readouts key off `screen` without breaking the promise that a DIRECT
 * session never changes: a direct server never reaches "hideout". Health,
 * needs and ammo are statements about a raid and go away in the cellar; the
 * hit marker and the interact prompt stay — the range uses both.
 */
function LimbPanel(): ReactElement | null {
  const screen = useHud((s) => s.screen);
  const limbs = useHud((s) => s.limbs);
  const maxLimbs = useHud((s) => s.maxLimbs);
  const bleedMask = useHud((s) => s.bleedMask);
  const fracMask = useHud((s) => s.fracMask);
  if (screen === "hideout") return null;

  return (
    <div
      style={{
        position: "absolute",
        left: 16,
        bottom: 16,
        width: LIMB_BOX_W,
        height: LIMB_BOX_H,
        font: FONT,
      }}
    >
      {LIMB_LAYOUT.map((rect, i) => {
        const hp = limbs[i] ?? 0;
        const max = maxLimbs[i] ?? 1;
        const bleeding = (bleedMask & (1 << i)) !== 0;
        const fractured = (fracMask & (1 << i)) !== 0;
        return (
          <div
            key={LIMB_IDS[i] ?? i}
            className={
              "limb" + (bleeding ? " limb-bleed" : "") + (fractured ? " limb-frac" : "")
            }
            title={`${LIMB_LABELS[i] ?? ""} ${Math.round(hp)}/${Math.round(max)}`}
            style={{
              position: "absolute",
              left: rect.left,
              top: rect.top,
              width: rect.width,
              height: rect.height,
              background: limbColor(hp / (max > 0 ? max : 1)),
            }}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hunger and thirst (protocol v26)
// ---------------------------------------------------------------------------

/** One meter's width, and the gap between the two. */
const NEEDS_BAR_W = 6;
const NEEDS_BAR_GAP = 4;

/** Fed and watered. Warm for the food, cool for the water — the pair has to be
 *  told apart in peripheral vision, and only the colour is doing that. */
const ENERGY_COLOR = "#d8a24a";
const HYDRATION_COLOR = "#5aa6d0";

/** Under `NEEDS_LOW`, where the body starts charging for it. */
const NEEDS_WARN_COLOR = "#d8663c";

/**
 * The two meters beside the limbs — hunger and thirst are health. Two thin
 * verticals, so they are never mistaken for a limb. No numbers: the meters
 * only matter in the last minutes of a long raid, and a full column says
 * "nothing yet" at a glance; the figure is a hover away. `null` renders
 * nothing — a pre-v26 server has no meters.
 */
function NeedsPanel(): ReactElement | null {
  const screen = useHud((s) => s.screen);
  const needs = useHud((s) => s.needs);
  if (needs === null || screen === "hideout") return null;

  return (
    <div
      style={{
        position: "absolute",
        left: 16 + LIMB_BOX_W + 10,
        bottom: 16,
        height: LIMB_BOX_H,
        display: "flex",
        gap: NEEDS_BAR_GAP,
        font: FONT,
        userSelect: "none",
      }}
    >
      {/* Energy first, hydration second — the order `Needs` declares them in,
          and the order they are printed in everywhere else. */}
      <NeedsBar
        value={needs.energy}
        label="Energy"
        color={ENERGY_COLOR}
        low="hungry: stamina recovers slower"
        empty="starving"
      />
      <NeedsBar
        value={needs.hydration}
        label="Hydration"
        color={HYDRATION_COLOR}
        low="thirsty: you move slower"
        empty="dehydrated"
      />
    </div>
  );
}

/**
 * One meter: a column that empties downwards (reads as what is left in a
 * bottle). The trough is `MedCastBar`'s. At zero it borrows `.limb-bleed` —
 * an empty meter IS a bleed, and the player already knows that ring.
 */
function NeedsBar(props: {
  value: number;
  label: string;
  color: string;
  /** Tooltip tail under `NEEDS_LOW`: what the body is charging for it. */
  low: string;
  /** ...and at zero, where it stops charging and starts killing. */
  empty: string;
}): ReactElement {
  const v = props.value > 0 ? (props.value < NEEDS_MAX ? props.value : NEEDS_MAX) : 0;
  const empty = v <= 0;
  const low = v < NEEDS_LOW;
  const tail = empty ? props.empty : low ? props.low : "";
  return (
    <div
      className={empty ? "limb-bleed" : undefined}
      title={`${props.label} ${Math.round(v)}/${NEEDS_MAX}${tail === "" ? "" : ` — ${tail}`}`}
      style={{
        width: NEEDS_BAR_W,
        height: "100%",
        borderRadius: 2,
        background: "rgba(10, 11, 13, 0.75)",
        boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.5)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
      }}
    >
      <div
        style={{
          width: "100%",
          height: `${(v / NEEDS_MAX) * 100}%`,
          background: low ? NEEDS_WARN_COLOR : props.color,
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ammunition
// ---------------------------------------------------------------------------

/**
 * The round counter — an account of what the player KNOWS. A fired-since-
 * checked magazine prints "?" and an unopened spare "+?". "?" is ignorance,
 * not danger: ordinary colour at lower opacity, low-ammo warning suppressed.
 */
function AmmoPanel(): ReactElement | null {
  const screen = useHud((s) => s.screen);
  const mag = useHud((s) => s.mag);
  const magSize = useHud((s) => s.magSize);
  const magKnown = useHud((s) => s.magKnown);
  const checking = useHud((s) => s.checking);
  const reserve = useHud((s) => s.reserve);
  const reserveUnknown = useHud((s) => s.reserveUnknown);
  const reloading = useHud((s) => s.reloading);
  if (screen === "hideout") return null;

  return (
    <div
      style={{
        position: "absolute",
        right: 18,
        bottom: 16,
        textAlign: "right",
        font: FONT,
        color: "#eaf1f8",
        textShadow: "0 1px 3px rgba(0,0,0,0.85)",
        userSelect: "none",
      }}
    >
      <div
        style={{
          fontSize: 26,
          fontWeight: 700,
          letterSpacing: "0.02em",
          // Below a third of a magazine the number itself is the warning — but
          // only when there IS a number. An unknown count cannot be low.
          color: magKnown && mag * 3 <= magSize && !reloading ? "#e0794f" : "#eaf1f8",
        }}
      >
        {/* The "?" sits a shade back from a real count: the same ink, quieter,
            which reads as "not known" where a colour change would read as
            "something is wrong". Dimmed on its own SPAN rather than on the block
            — opacity composes down the tree, and on the block it would drag the
            reserve beside it darker for a reason that has nothing to do with the
            reserve. */}
        {magKnown ? mag : <span style={{ opacity: 0.8 }}>?</span>}
        {/* The reserve is what the next reload can actually reach AND count — the
            rounds in your spare MAGAZINES, since pressing R swaps a box rather
            than filling one. A number you plan with, not one you shoot with, so
            it is dimmed and never takes the low-ammo colour. The "+?" says there
            is at least one more box whose contents nobody has looked at, so the
            number in front of it is a floor rather than a total. */}
        <span style={{ fontSize: 15, opacity: 0.62 }}>
          {` / ${reserve}${reserveUnknown ? "+?" : ""}`}
        </span>
      </div>
      {/* One line, three states. The reload wins the slot: the server refuses a
          check while one is running, so the two can only overlap on the tail of
          a play-out — and "Reloading…" is the more urgent of the two facts. */}
      <div style={{ height: 14, opacity: 0.85 }}>
        {reloading ? "Reloading…" : checking ? "Checking…" : ""}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The raid clock and the way out
// ---------------------------------------------------------------------------

/**
 * How long the raid still has. The deadline is a local `performance.now()`
 * from `welcome`, so the clock never stutters with the network. Ticks once a
 * second. Absent on a direct server.
 */
function RaidTimer(): ReactElement | null {
  const endsAt = useHud((s) => s.raidEndsAtMs);
  const [now, setNow] = useState(() => performance.now());

  useEffect(() => {
    if (endsAt === null) return;
    // Twice a second, so the displayed second never lags by more than half of
    // itself.
    const timer = window.setInterval(() => {
      setNow(performance.now());
    }, 500);
    return () => {
      window.clearInterval(timer);
    };
  }, [endsAt]);

  if (endsAt === null) return null;
  const leftMs = endsAt - now;

  return (
    <div
      style={{
        position: "absolute",
        left: "50%",
        top: 14,
        transform: "translateX(-50%)",
        font: FONT,
        // The last two minutes are the ones that change decisions, so that is
        // when the number stops being furniture.
        fontSize: 20,
        letterSpacing: "0.06em",
        color: leftMs <= 120_000 ? "#e0794f" : "#eaf1f8",
        textShadow: "0 1px 3px rgba(0,0,0,0.85)",
        userSelect: "none",
      }}
    >
      {formatMmSs(leftMs)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The squad (protocol v24)
// ---------------------------------------------------------------------------

/** The mint, once, as CSS. Shared with the name tags and the diamonds. */
const TEAM_CSS = cssHex(TEAM_COLOR);

/**
 * Who you came in with. Drawn from the PARTY, not the snapshot, so the strip
 * is complete from the first frame and a squadmate behind a hill still has a
 * line. The raid contributes only `squadDead`, matched by display name.
 * Absent for a solo, and `party` is null on a direct server.
 */
function SquadStrip(): ReactElement | null {
  const party = useHud((s) => s.party);
  const me = useHud((s) => s.me);
  const dead = useHud((s) => s.squadDead);
  const screen = useHud((s) => s.screen);

  if (screen !== "raid") return null;
  if (party === null || party.members.length < 2) return null;

  return (
    <div
      style={{
        position: "absolute",
        left: 16,
        top: 14,
        minWidth: 116,
        padding: "5px 9px",
        borderRadius: 4,
        font: FONT,
        color: TEXT,
        background: PANEL_BG,
        border: `1px solid ${PANEL_LINE}`,
        userSelect: "none",
      }}
    >
      {party.members.map((m) => {
        const isMe = m.username === me;
        const isDead = dead.includes(m.name);
        return (
          <div
            key={m.username}
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 6,
              whiteSpace: "nowrap",
              // A dead squadmate stays on the strip and loses its colour —
              // removing the line would read as "he extracted".
              color: isDead ? TEXT_DIM : TEXT,
              textDecoration: isDead ? "line-through" : "none",
            }}
          >
            {/* The same diamond that floats over the man in the world, so the
                strip and the valley are read with one symbol rather than two. */}
            <span
              style={{
                width: 6,
                height: 6,
                flex: "none",
                transform: "rotate(45deg)",
                background: isDead ? TEXT_DIM : TEAM_CSS,
              }}
            />
            <span>{m.name}</span>
            {/* Which line is you. Nothing else distinguishes it: your own entity
                is never flagged `team` to yourself, so you can never be crossed
                out here, and a second colour for one line of four is noise. */}
            {isMe ? <span style={{ color: TEXT_DIM }}>you</span> : null}
          </div>
        );
      })}
    </div>
  );
}

/**
 * "Extract: Bahnhofstraße — 7 s remaining". `secLeft` is the server's
 * countdown, re-sent every snapshot; the field simply stops arriving.
 */
function ExtractReadout(): ReactElement | null {
  const extract = useHud((s) => s.extract);
  if (extract === null) return null;

  const secs = Math.max(0, Math.ceil(extract.secLeft));

  return (
    <div
      style={{
        position: "absolute",
        left: "50%",
        top: 44,
        transform: "translateX(-50%)",
        padding: "5px 12px",
        borderRadius: 4,
        font: FONT,
        color: TEXT,
        background: PANEL_BG,
        border: "1px solid rgba(140, 220, 148, 0.55)",
        whiteSpace: "nowrap",
        userSelect: "none",
      }}
    >
      <span style={{ color: TEXT_DIM }}>Extract:</span> {extract.name}{" "}
      <span style={{ color: "#8fd39a" }}>— {secs} s remaining</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Interaction and medicine
// ---------------------------------------------------------------------------

/**
 * `[F] Open crate`. The label is the game's, the key cap the HUD's — what keeps
 * the keybinding out of `game/`.
 */
function InteractPrompt(): ReactElement | null {
  const text = useHud((s) => s.interactPrompt);
  if (text === null) return null;

  return (
    <div
      style={{
        position: "absolute",
        left: "50%",
        bottom: 104,
        transform: "translateX(-50%)",
        padding: "5px 11px",
        borderRadius: 4,
        font: FONT,
        color: TEXT,
        background: PANEL_BG,
        border: `1px solid ${PANEL_LINE}`,
        whiteSpace: "nowrap",
        userSelect: "none",
      }}
    >
      {/* F, not E: E leans right since protocol v17 (`input/input.ts`). */}
      <span style={{ opacity: 0.55 }}>[F]</span> {text}
    </div>
  );
}

/**
 * The bandage timer: a bar draining to nothing at `endMs`. No label. Width is
 * written to the DOM from a rAF loop, not state — the one widget that changes
 * every frame. Unmounting cancels the loop.
 */
function MedCastBar(): ReactElement | null {
  const cast = useHud((s) => s.medCast);
  const fill = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (cast === null) return;
    let raf = 0;
    const step = (): void => {
      const el = fill.current;
      if (el !== null) {
        const left = (cast.endMs - performance.now()) / cast.durMs;
        const clamped = left < 0 ? 0 : left > 1 ? 1 : left;
        el.style.width = `${(clamped * 100).toFixed(1)}%`;
      }
      raf = requestAnimationFrame(step);
    };
    step();
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [cast]);

  if (cast === null) return null;

  return (
    <div
      style={{
        position: "absolute",
        left: "50%",
        bottom: 134,
        transform: "translateX(-50%)",
        width: 190,
        height: 4,
        borderRadius: 2,
        background: "rgba(10, 11, 13, 0.75)",
        boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.5)",
        overflow: "hidden",
      }}
    >
      <div
        ref={fill}
        style={{ width: "100%", height: "100%", background: "#d8e2e6" }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flashbang (protocol v24)
// ---------------------------------------------------------------------------

/**
 * Fraction of the white-out at FULL white before the fade — a flash fading at
 * once reads as a bright frame, not blindness.
 */
const FLASH_HOLD_FRAC = 0.3;

/**
 * The white-out, driven by rAF writing `style.opacity` like `MedCastBar`.
 * `pointerEvents: "none"` is load-bearing — the inventory and death overlays
 * live above this.
 */
function FlashOverlay(): ReactElement | null {
  const flash = useHud((s) => s.flash);
  const el = useRef<HTMLDivElement | null>(null);
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (flash === null) {
      setLive(false);
      return;
    }
    setLive(true);
    let raf = 0;
    const step = (): void => {
      const left = (flash.endMs - performance.now()) / flash.durMs;
      if (left <= 0) {
        setLive(false);
        return;
      }
      const node = el.current;
      if (node !== null) {
        const k = left > 1 - FLASH_HOLD_FRAC ? 1 : left / (1 - FLASH_HOLD_FRAC);
        node.style.opacity = (k * k).toFixed(3);
      }
      raf = requestAnimationFrame(step);
    };
    step();
    return () => {
      cancelAnimationFrame(raf);
    };
  }, [flash]);

  if (flash === null || !live) return null;

  return (
    <div
      ref={el}
      style={{
        position: "absolute",
        inset: 0,
        // Not pure white: a faint warm cast reads as a burn rather than a
        // crash.
        background: "#fffaf2",
        opacity: 1,
        pointerEvents: "none",
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Hit marker
// ---------------------------------------------------------------------------

/** Per-kind look: a predicted hit is a whisper, a kill is a shout. */
const MARKER_STYLE: Record<
  HitMarkerKind,
  { color: string; scale: number; opacity: number; gap: number }
> = {
  predicted: { color: "#dfe8f0", scale: 0.8, opacity: 0.5, gap: 4 },
  hit: { color: "#ffffff", scale: 1, opacity: 0.95, gap: 5 },
  kill: { color: "#e2493c", scale: 1.25, opacity: 1, gap: 6 },
};

/** How long the marker stays on screen, ms. */
const MARKER_LIFE_MS = 320;

function HitMarker(): ReactElement | null {
  const marker = useHud((s) => s.hitMarker);
  const [visibleAt, setVisibleAt] = useState(0);

  useEffect(() => {
    if (marker.at === 0) return;
    setVisibleAt(marker.at);
    const timer = setTimeout(() => {
      setVisibleAt(0);
    }, MARKER_LIFE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [marker.at]);

  if (visibleAt === 0) return null;
  const s = MARKER_STYLE[marker.kind];

  return (
    // Two nested elements: the outer carries the per-kind size, the inner the
    // animation, or the keyframes' `transform` would overwrite the scale. `key`
    // is the timestamp — remounting restarts the animation.
    <div
      key={marker.at}
      style={{
        position: "absolute",
        left: "50%",
        top: "50%",
        width: 26,
        height: 26,
        marginLeft: -13,
        marginTop: -13,
        opacity: s.opacity,
        transform: `scale(${s.scale})`,
      }}
    >
      <div className="hitmark" style={{ position: "absolute", inset: 0 }}>
        {[45, 135, 225, 315].map((deg) => (
          <span
            key={deg}
            style={{
              position: "absolute",
              left: 12,
              top: 12,
              width: 2,
              height: 9,
              background: s.color,
              transformOrigin: "50% 0",
              transform: `rotate(${deg}deg) translateY(${s.gap}px)`,
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Death
// ---------------------------------------------------------------------------

/** The line under the headline: who, or what killed you when nobody did. */
export function causeLine(death: DeathInfo): string {
  if (death.cause === "bleed") return "Bled out";
  if (death.cause === "starve") return "Starved";
  return death.killerName === "" ? "by an unknown shooter" : `by ${death.killerName}`;
}

/** Weapon · limb · range, whichever of the three the server knew. */
export function forensicsLine(death: DeathInfo): string {
  const parts: string[] = [];
  if (death.weapon !== "") parts.push(death.weapon);
  if (death.limb !== "") parts.push(death.limb);
  // Under a metre still reads as "1 m": a zero would look like a bug.
  if (death.distM !== null) parts.push(`${Math.max(1, Math.round(death.distM))} m`);
  return parts.join(" · ");
}

function DeathOverlay(props: HudProps): ReactElement | null {
  const dead = useHud((s) => s.dead);
  const death = useHud((s) => s.death);
  const mode = useHud((s) => s.mode);

  // The killcam has no store event of its own when it ends — one timer per
  // death re-renders the overlay at exactly that moment.
  const until = death?.killcamUntilMs ?? null;
  const [frozen, setFrozen] = useState(false);
  useEffect(() => {
    if (until === null) {
      setFrozen(false);
      return;
    }
    const leftMs = until - performance.now();
    if (leftMs <= 0) {
      setFrozen(false);
      return;
    }
    setFrozen(true);
    const timer = setTimeout(() => {
      setFrozen(false);
    }, leftMs);
    return () => {
      clearTimeout(timer);
    };
  }, [until]);

  if (!dead) return null;

  // Respawn is direct-mode only: on a meta server dying ends the raid and the
  // summary screen covers this within a tick.
  const canRespawn = mode !== "meta" && !frozen;
  const forensics = death === null ? "" : forensicsLine(death);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 14,
        // Nearly clear while the killcam runs: the frozen shot of the killer
        // IS the point, and the text has to sit over it rather than hide it.
        background: frozen ? "rgba(10, 8, 8, 0.28)" : "rgba(10, 8, 8, 0.62)",
        color: "#f0e6e2",
        font: FONT,
        // The ONLY part of the HUD that takes the mouse — the overlay root is
        // also what swallows stray clicks so they cannot reach the canvas.
        pointerEvents: "auto",
        userSelect: "none",
      }}
    >
      <div style={{ fontSize: 40, fontWeight: 700, letterSpacing: "0.08em" }}>
        Killed in action
      </div>
      <div style={{ fontSize: 14, opacity: 0.8, minHeight: 18 }}>
        {death === null ? "" : causeLine(death)}
      </div>
      {forensics === "" ? null : (
        <div style={{ fontSize: 13, opacity: 0.6, letterSpacing: "0.04em" }}>{forensics}</div>
      )}
      {canRespawn ? (
        <button type="button" className="respawn" onClick={props.onRespawn}>
          Respawn
        </button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

const HUD_CSS = `
.limb {
  border: 1px solid rgba(0, 0, 0, 0.55);
  border-radius: 2px;
  box-sizing: border-box;
}
/* Bleeding: a pulsing red ring. Motion is the cue — a static tint would be
   mistaken for the damage colour itself. */
.limb-bleed {
  animation: rietz-bleed 900ms ease-in-out infinite;
}
/* Fractured: a broken (dashed) outline. Reads instantly as "not whole". */
.limb-frac {
  border-style: dashed;
  border-color: #f2f2f2;
}
@keyframes rietz-bleed {
  0%, 100% { box-shadow: 0 0 0 0 rgba(220, 40, 40, 0.9); }
  50%      { box-shadow: 0 0 0 3px rgba(220, 40, 40, 0.0); }
}
.hitmark {
  animation: rietz-hit 320ms ease-out forwards;
}
@keyframes rietz-hit {
  0%   { transform: scale(0.55); opacity: 1; }
  35%  { opacity: 1; }
  100% { opacity: 0; }
}
.respawn {
  font: 600 15px/1 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  letter-spacing: 0.06em;
  color: #f5efe9;
  background: rgba(40, 44, 50, 0.9);
  border: 1px solid rgba(220, 210, 200, 0.35);
  border-radius: 4px;
  padding: 10px 20px;
  cursor: pointer;
}
.respawn:hover {
  background: rgba(62, 68, 76, 0.95);
  border-color: rgba(220, 210, 200, 0.6);
}
`;
